import React, { useContext, useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography
} from '@mui/material';
import { AxiosError } from 'axios';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { response } from '../../../api/db';
import { userWithdraw } from '../../../api/db/user';
import {
  gray100,
  gray90,
  primary100,
  primaryButtonBackgroundColor,
  primaryColor
} from '../../../constants/Colors';
import { withdrawList } from '../../../constants/Enum';
import { sideBarWidth, spHeaderHeight } from '../../../constants/Location';
import { withdrawalReasonList } from '../../../constants/SelectOptions';
import { AuthContext } from '../../../contexts/auth';
import { accountWithdrawalSchema } from '../../../validation/user';
import { AccountWithdrawalType } from '../../../validation/user/userType';
import { common } from '../../../modules/error';

import CustomTextfield from '../../uiParts/CustomTextfield';
import CustomSelectField from '../../uiParts/CustomSelectField';
import CustomLoading from '../../uiParts/CustomLoading';
import SideBar from '../../uiParts/SideBar';
import CustomSnackbar from '../../uiParts/CustomSnackbar';
import CustomHead from '../CustomHead';
import { sentryLog } from '../../../config/sentry';
import { MyProductGetProductType } from '../../../validation/products/productType';
import { getMemberProductList } from '../../../api/db/product';
import { getMemberList } from '../../../api/db/member';

const styles = {
  container: {
    '@media screen and (min-width:961px)': {
      ml: sideBarWidth
    },
    '@media screen and (max-width:960px)': {
      mt: spHeaderHeight
    },
    direction: 'column',
    justifyContent: 'center',
    mb: '40px'
  },
  body: {
    '@media screen and (max-width:600px)': {
      width: '340px',
      mt: '24px'
    },
    textAlign: 'left',
    maxWidth: '560px',
    width: '95%',
    mt: '32px'
  },
  title: {
    '@media screen and (max-width:600px)': {
      fontSize: '24px'
    },
    fontSize: '32px',
    fontWeight: 500,
    mb: '24px',
    color: gray100
  },
  text: {
    fontSize: '16px',
    fontWeight: 400,
    mb: '48px',
    color: gray100
  },
  listLabel: {
    color: gray100,
    fontSize: '16px',
    fontWeight: 500,
    mb: '8px'
  },
  list: {
    color: gray100,
    fontSize: '16px',
    fontWeight: 400,
    whiteSpace: 'pre-line'
  },
  checkbox: {
    '& .MuiSvgIcon-root': { fontSize: '24px' },
    '&.Mui-checked': {
      color: primaryColor
    },
    color: gray90
  },
  checkboxLabel: {
    mt: '24px',
    color: gray100,
    fontWeight: 400,
    fontSize: '16px'
  },
  button: {
    width: '100%',
    mt: '40px',
    fontSize: '16px',
    fontWeight: 500,
    ...primaryButtonBackgroundColor
  },
  link: {
    color: primary100,
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '700',
    textDecoration: 'underline'
  }
};

const AccountWithdrawal: React.FC = () => {
  // ユーザーのログインチェック
  const { isLoggedIn, withdraw, userId } = useContext(AuthContext);
  useEffect(() => {
    if (!isLoggedIn) navigate('/auth/login');
  }, [isLoggedIn]);

  const [isChecked, setIsChecked] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');
  const navigate = useNavigate();

  const {
    control,
    formState: { errors },
    handleSubmit,
    watch
  } = useForm<AccountWithdrawalType>({
    resolver: yupResolver(accountWithdrawalSchema),
    defaultValues: {
      reasonSelectedOption: 0
    }
  });

  const isButtonDisabled = (): boolean => {
    if (watch('reasonSelectedOption') === withdrawList.other) {
      return (
        !isChecked ||
        watch('reasonSelectedOption') === 0 ||
        watch('reasonDetail') === '' ||
        Boolean(errors.reasonSelectedOption) ||
        Boolean(errors.reasonDetail)
      );
    }
    return (
      !isChecked ||
      watch('reasonSelectedOption') === 0 ||
      Boolean(errors.reasonSelectedOption) ||
      Boolean(errors.reasonDetail)
    );
  };

  const onSubmit = async (): Promise<void> => {
    try {
      setIsLoading(true);
      if (withdraw === undefined) {
        throw new Error('withdraw is undefined');
      }
      const response = await userWithdraw(
        watch('reasonSelectedOption'),
        watch('reasonDetail')
      );
      if (response.status === 204) {
        withdraw();
      }
      navigate('/account/withdraw/complete');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      const axiosError = error as AxiosError;
      if (axiosError.isAxiosError && axiosError.response) {
        const errorResponse = axiosError.response as response;
        setErrorText(errorResponse.data.message);
        setOpen(true);
      } else {
        setErrorText(common);
        setOpen(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const [amountOfProduct, setAmountOfProduct] = React.useState(0);
  const [amountOfMember, setAmountOfMember] = React.useState(0);
  const [products, setProducts] = React.useState<MyProductGetProductType[]>([]);

  useEffect(() => {
    void (async () => {
      if (!userId) return;
      setIsLoading(true);
      const response = await getMemberList();
      setAmountOfMember(Object.keys(response.data.members).length);
      const limit = 5;
      const offset = 0;
      const order = 1;
      const method = 1;
      const fetchData = await getMemberProductList(
        limit,
        offset,
        order,
        method,
        userId
      );
      setProducts(fetchData.products);
      setAmountOfProduct(fetchData.total_count);
      setIsLoading(false);
    })();
  }, [userId]);

  return (
    <>
      <CustomHead title="退会申請 | CMCOS[コムコス]" />
      <Grid container>
        <CustomLoading loading={isLoading} />
        <SideBar />
        <Grid container sx={styles.container}>
          <Grid item sx={styles.body}>
            <Typography sx={styles.title}>退会申請</Typography>
            <Typography sx={styles.text}>
              退会をご希望の方は、下記アンケートにご協力の上、「退会申請する」ボタンを押してください。
            </Typography>
            <Controller
              name="reasonSelectedOption"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <CustomSelectField
                  {...field}
                  value={field.value}
                  onChange={field.onChange}
                  required
                  requiredText="必須"
                  error={Boolean(errors.reasonSelectedOption)}
                  helperText={errors.reasonSelectedOption?.message}
                  list={withdrawalReasonList}
                  labelText="退会理由"
                  sx={{ maxWidth: '560px' }}
                  formMb="24px"
                />
              )}
            />
            <Controller
              name="reasonDetail"
              control={control}
              render={({ field }) => (
                <CustomTextfield
                  {...field}
                  {...(watch('reasonSelectedOption') === withdrawList.other && {
                    required: true
                  })}
                  {...(watch('reasonSelectedOption') === withdrawList.other && {
                    requiredText: '必須'
                  })}
                  error={Boolean(errors.reasonDetail)}
                  helperText={errors.reasonDetail?.message}
                  labelText="詳しい理由"
                  formMb="16px"
                  width="100%"
                  sx={{ maxWidth: '560px' }}
                  fieldType="textareaRow6"
                  multiline
                  rows={6}
                />
              )}
            />

            <Typography component="span" sx={styles.listLabel}>
              ご注意
            </Typography>
            <Typography component={'p'} sx={styles.list}>
              ・退会すると、CMCOSをご利用いただけない状態になります。
            </Typography>
            <Typography component={'p'} sx={styles.list}>
              ・登録したユーザー情報や建材情報は削除されます。
            </Typography>
            <Typography component={'p'} sx={styles.list}>
              ・退会を取り消しすることはできません。
            </Typography>
            {amountOfProduct !== 0 ? (
              <>
                {amountOfMember > 1 ? (
                  <>
                    {amountOfProduct > 5 ? (
                      <Typography sx={styles.list}>
                        {`・現在、以下を含む${amountOfProduct}件の建材を担当しています。\n　退会した場合、代表者権限のユーザーが建材の担当として設定されます。`}
                      </Typography>
                    ) : (
                      <Typography sx={styles.list}>
                        {`・現在、以下の${amountOfProduct}件の建材を担当しています。\n　退会した場合、代表者権限のユーザーが建材の担当として設定されます。`}
                      </Typography>
                    )}

                    <Grid
                      container
                      flexDirection={'column'}
                      mt={'24px'}
                      ml={'16px'}
                      gap={'10px'}
                    >
                      {products &&
                        products.map(
                          (product: MyProductGetProductType, index: number) => (
                            <Grid container flexDirection={'row'}>
                              <Typography
                                sx={{
                                  color: primary100,
                                  fontSize: '16px',
                                  fontWeight: '700'
                                }}
                              >
                                ・
                              </Typography>
                              <Typography
                                key={index}
                                onClick={() =>
                                  navigate(`/material/${product.id}`, {
                                    state: {
                                      productInfoId: product.product_info_id
                                    }
                                  })
                                }
                                sx={styles.link}
                              >
                                {product.name}
                              </Typography>
                            </Grid>
                          )
                        )}
                    </Grid>
                  </>
                ) : null}
              </>
            ) : null}

            <FormControlLabel
              control={
                <Checkbox
                  sx={styles.checkbox}
                  checked={isChecked}
                  onChange={(event) => {
                    setIsChecked(event.target.checked);
                  }}
                />
              }
              label="上記の注意事項に同意する"
              sx={styles.checkboxLabel}
            />
            <Button
              variant="contained"
              sx={styles.button}
              disabled={isButtonDisabled()}
              onClick={handleSubmit(async () => {
                await onSubmit();
              })}
            >
              退会する
            </Button>
          </Grid>
        </Grid>
        <CustomSnackbar
          open={open}
          autoHideDuration={6000}
          onClose={() => setOpen(false)}
          text={errorText}
        />
      </Grid>
    </>
  );
};

export default AccountWithdrawal;
